import { useTheme } from '@mui/material/styles';
// src/components/reader/SingleReaderPanel.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { transformArticleHTML } from '../../utils/html/transformHtml';

import { Release } from '../../models/Release';
import Loader from "../other/Loader";

export default function SingleReaderPanel({
    articleId: propArticleId,
    selectedRelease: propRelease,
    fetchArticle,
    handleArticleSelect,
    onClose,
    isSinglePageView = false,
}) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const containerRef = useRef(null);

    // State für Artikel
    const [articleDoc, setArticleDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Parameter: erst Props, sonst URL
    const qs = new URLSearchParams(window.location.search);
    const articleId = propArticleId ?? qs.get('articleId');
    const urlRelease = useMemo(() => Release.fromUrl(), []);
    const selectedRelease = useMemo(
        () => propRelease ? Release.from(propRelease) : urlRelease,
        [propRelease, urlRelease],
    );

    // Remove the Single Parameter for the "real" help (not single page view)
    function getFullHelpUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('single');
        return url.toString();
    }

    // Reload only when the article or the release specific fetch callback changes.
    useEffect(() => {
        if (!articleId) {
            setError('No articleId supplied.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        (async () => {
            await fetchArticle(articleId, (body) => {
                setArticleDoc(body);
            });
            setIsLoading(false);
        })();
    }, [articleId, fetchArticle]);

    // Derive rendered content directly from the loaded document and release.
    const articleNodes = useMemo(() => {
        if (!articleDoc) return null;
        const rawHtml = articleDoc.body || '';
        return transformArticleHTML(
            rawHtml,
            selectedRelease,
            [],             // kein TOC
            false        // offlineMode
        );
    }, [articleDoc, selectedRelease]);

    // 3) Glossary-Linking & Klick-Handler
    useEffect(() => {
        if (!articleNodes) return;
        const container = containerRef.current;
        if (!container) return;

        // a) Spans ersetzen
        container.querySelectorAll('span[data-glossaryitemid]').forEach(span => {
            const topicId = span.getAttribute('data-glossaryitemid');
            const a = document.createElement('a');
            a.href = '#';
            a.className = 'glossary-link';
            a.setAttribute('data-glossaryitemid', topicId);
            a.textContent = span.textContent;
            span.parentNode.replaceChild(a, span);
        });

        // b) Klick abfangen
        const onClick = async e => {
            let t = e.target;
            if (t.nodeType === Node.TEXT_NODE) t = t.parentElement;
            const el = t.closest('a[data-glossaryitemid]');
            if (!el) return;

            e.preventDefault();

            // Go to glossary entry
            const topicId = el.getAttribute('data-glossaryitemid');
            handleArticleSelect(topicId);
            onClose();
        };

        container.addEventListener('click', onClick);
        return () => container.removeEventListener('click', onClick);
    }, [articleNodes, handleArticleSelect, onClose]);

    // Loader & Error
    if (isLoading) {
        return (
            <Loader
                marginCustom='10px 0 0 0'
            />
        );
    }
    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 2 }}>Error</Typography>
                <Typography>{error}</Typography>
            </Box>
        );
    }

    // Render
    return (
        <>
            <Box
                // Apply dark mode to the HTML preview
                className={darkMode ? 'dark-mode' : ''}
                ref={containerRef}
                sx={{

                    overflowY: 'auto',
                    p: 3,
                    '&::-webkit-scrollbar': { width: 8 },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#9aa', borderRadius: 4 },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#9aa transparent',

                    // links use brand accent in dark mode
                    ...(darkMode
                        ? { '& a': { color: 'accent.main' } }
                        : {}
                    ),
                }}
            >
                <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="h4">{articleDoc.title}</Typography>
                    <Box sx={{ textAlign: 'left', ml: 2, my: 2 }}>
                        {isSinglePageView && (
                            <Button
                                variant="contained"
                                sx={{ bgcolor: theme.palette.primary.main, color: 'common.white' }}
                                href={getFullHelpUrl()}
                                target="_blank"
                                rel="noopener"
                                endIcon={<LaunchIcon fontSize="small" />}
                            >
                                Open in Online Help
                            </Button>
                        )}
                    </Box>
                </Box>
                <Box className="article-section">
                    {articleNodes}
                </Box>
            </Box>
        </>
    );
}

